// src/components/LabQuestion.jsx
// Composant complet : topologie + terminal + tâches + vérification
import React, { useState, useCallback, useMemo } from "react";
import {
  Box, Grid, Typography, Paper, Stack, Chip, Button,
  Divider, Alert, LinearProgress, alpha,
} from "@mui/material";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayArrowIcon    from "@mui/icons-material/PlayArrow";
import TerminalIcon     from "@mui/icons-material/Terminal";
import AccountTreeIcon  from "@mui/icons-material/AccountTree";
import CiscoTerminal, { createDevice } from "./CiscoTerminal";
import LabTopology      from "./LabTopology";

// ── Vérificateur de tâches ────────────────────────────────────────────────────
function checkTask(task, deviceStates) {
  const dev = deviceStates[task.device];
  if (!dev) return false;

  switch (task.check) {
    case "ip_configured": {
      // Vérifie qu'une interface a l'IP attendue
      return Object.values(dev.interfaces).some(
        ifc => ifc.ip === task.expected_ip
      );
    }
    case "interface_up": {
      const ifc = Object.entries(dev.interfaces).find(
        ([name]) => name.toLowerCase().includes(task.interface?.toLowerCase())
      );
      return ifc ? ifc[1].up === true : false;
    }
    case "ospf_configured": {
      return dev.ospf !== null && dev.ospf?.pid !== undefined;
    }
    case "ospf_network": {
      return (dev.ospf?.networks || []).some(n =>
        n.includes(task.expected_network || "")
      );
    }
    case "hostname": {
      return dev.hostname_val === task.expected_hostname;
    }
    case "static_route": {
      return dev.routes.some(r => r.includes(task.expected_network || ""));
    }
    case "rip_configured": {
      return dev.rip !== null;
    }
    case "enable_password": {
      return dev.enable_pass === task.expected_password;
    }
    default:
      return false;
  }
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function LabQuestion({
  question,           // { content, lab_data: { topology, tasks, scenario } }
  onComplete = null,  // callback(allTasksDone)
  readOnly = false,
  hideScenario = false, // masque l'énoncé si déjà affiché par le parent
}) {
  const lab = question?.lab_data || {};
  const topology = lab.topology || { devices: [], links: [] };
  const tasks    = lab.tasks    || [];
  const scenario = lab.scenario || "";

  // Initialiser un device state par équipement
  const [deviceStates, setDeviceStates] = useState(() => {
    const states = {};
    (topology.devices || []).forEach(d => {
      states[d.id] = d.initial_config
        ? { ...createDevice(d.id), ...d.initial_config }
        : createDevice(d.id);
    });
    return states;
  });

  const [activeDevice, setActiveDevice] = useState(
    topology.devices?.[0]?.id || null
  );
  const [view, setView] = useState("split"); // "split" | "topo" | "terminal"

  // Calculer l'état des tâches
  const taskStatus = useMemo(() =>
    tasks.map(t => ({ ...t, done: checkTask(t, deviceStates) })),
    [tasks, deviceStates]
  );

  const doneCount = taskStatus.filter(t => t.done).length;
  const allDone   = doneCount === tasks.length && tasks.length > 0;
  const progress  = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;

  const onDeviceStateChange = useCallback((deviceId, newState) => {
    setDeviceStates(prev => ({ ...prev, [deviceId]: newState }));
    onComplete?.(allDone);
  }, [allDone, onComplete]);

  const currentDeviceObj = topology.devices?.find(d => d.id === activeDevice);

  return (
    <Box>
      {/* ── Énoncé ── */}
      {!hideScenario && (
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, lineHeight: 1.7 }}>
          {question?.content}
        </Typography>
      )}

      {/* ── Scénario ── */}
      {!hideScenario && scenario && (
        <Alert icon={<AccountTreeIcon />} severity="info" sx={{ mb: 2, borderRadius: "8px" }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{scenario}</Typography>
        </Alert>
      )}

      {/* ── Tâches ── */}
      <Paper sx={{
        mb: 2, p: 2, borderRadius: "10px",
        background: alpha("#0d1117", 0.04),
        border: "1px solid",
        borderColor: allDone ? alpha("#3fb950", 0.4) : "divider",
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Tâches à accomplir
          </Typography>
          <Chip
            label={`${doneCount}/${tasks.length}`}
            size="small"
            sx={{
              fontWeight: 800,
              background: allDone ? alpha("#3fb950", 0.15) : alpha("#58a6ff", 0.1),
              color: allDone ? "#3fb950" : "#58a6ff",
            }}
          />
        </Stack>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 1.5, height: 6, borderRadius: 3,
            background: alpha("#58a6ff", 0.1),
            "& .MuiLinearProgress-bar": {
              background: allDone ? "#3fb950" : "#58a6ff",
              borderRadius: 3,
            },
          }}
        />

        <Stack spacing={1}>
          {taskStatus.map((t, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              {t.done
                ? <CheckCircleIcon sx={{ fontSize: 18, color: "#3fb950", mt: .2, flexShrink: 0 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#8b949e", mt: .2, flexShrink: 0 }} />
              }
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{
                  color: t.done ? "#3fb950" : "text.primary",
                  fontWeight: t.done ? 600 : 400,
                  textDecoration: t.done ? "line-through" : "none",
                  opacity: t.done ? 0.7 : 1,
                }}>
                  {t.desc}
                </Typography>
                <Typography variant="caption" sx={{ color: "#8b949e", fontFamily: "monospace" }}>
                  {t.device}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* ── Sélection équipement ── */}
      {topology.devices?.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", mr: .5 }}>
            Terminal :
          </Typography>
          {topology.devices.map(d => (
            <Chip
              key={d.id}
              label={d.id}
              size="small"
              icon={<TerminalIcon sx={{ fontSize: "0.85rem !important" }} />}
              onClick={() => setActiveDevice(d.id)}
              sx={{
                cursor: "pointer",
                fontWeight: activeDevice === d.id ? 700 : 400,
                background: activeDevice === d.id ? alpha("#58a6ff", 0.15) : "transparent",
                color: activeDevice === d.id ? "#58a6ff" : "text.secondary",
                border: `1px solid ${activeDevice === d.id ? "#58a6ff" : "transparent"}`,
              }}
            />
          ))}
        </Stack>
      )}

      {/* ── Vue principale : Topologie + Terminal ── */}
      <Grid container spacing={2}>
        {/* Topologie */}
        <Grid item xs={12} md={5}>
          <Typography variant="caption" color="text.secondary" sx={{
            display: "block", mb: .75, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: "0.07em",
          }}>
            Topologie réseau
          </Typography>
          <LabTopology
            devices={topology.devices}
            links={topology.links}
            activeDevice={activeDevice}
            onSelectDevice={readOnly ? null : setActiveDevice}
            height={280}
          />
        </Grid>

        {/* Terminal */}
        <Grid item xs={12} md={7}>
          <Typography variant="caption" color="text.secondary" sx={{
            display: "block", mb: .75, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: "0.07em",
          }}>
            Terminal — {activeDevice}
          </Typography>
          {activeDevice && (
            <CiscoTerminal
              key={activeDevice}
              deviceId={activeDevice}
              initialHostname={currentDeviceObj?.label || activeDevice}
              initialConfig={deviceStates[activeDevice]}
              onStateChange={(newState) => onDeviceStateChange(activeDevice, newState)}
              height={280}
              readOnly={readOnly}
            />
          )}
        </Grid>
      </Grid>

      {/* ── Succès ── */}
      {allDone && (
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ mt: 2, borderRadius: "10px", fontWeight: 600 }}
        >
          Toutes les tâches sont accomplies ! Configuration validée.
        </Alert>
      )}
    </Box>
  );
}